import type { FC, SVGProps } from 'react';

import type { DealStage } from '@/api/pipelines/types';
import CircleCheckBigIcon from '@/assets/svg/ui-elements/circle-check-big.svg';
import CircleDollarSignIcon from '@/assets/svg/ui-elements/circle-dollar-sign.svg';
import CircleXIcon from '@/assets/svg/ui-elements/circle-x-outline.svg';
import HandshakeIcon from '@/assets/svg/ui-elements/handshake.svg';
import ReceiptTextIcon from '@/assets/svg/ui-elements/receipt-text.svg';
import ScaleIcon from '@/assets/svg/ui-elements/scale.svg';
import SignatureIcon from '@/assets/svg/ui-elements/signature.svg';
import StarIcon from '@/assets/svg/ui-elements/star-outline.svg';
import UserCheckIcon from '@/assets/svg/ui-elements/user-check.svg';

import type { DealKanbanSource, DealKanbanStructure } from './types';

/** Column color treatment: info = entry stage, success/error = terminal stages */
export type DealKanbanStageTone = 'info' | 'default' | 'success' | 'error';

export type DealKanbanStageConfig = {
  id: DealStage;
  label: string;
  tone: DealKanbanStageTone;
  terminal: boolean;
  /**
   * The backend moves cards here itself and rejects every manual move into or out of the stage,
   * so the board never offers one.
   */
  systemOnly?: boolean;
  Icon: FC<SVGProps<SVGSVGElement>>;
};

export const DEAL_KANBAN_STAGES: DealKanbanStageConfig[] = [
  { id: 'Interested', label: 'Interested', tone: 'info', terminal: false, Icon: StarIcon },
  { id: 'NdaSigned', label: 'NDA Signed', tone: 'default', terminal: false, Icon: SignatureIcon },
  {
    id: 'BuyerSellerConnected',
    label: 'B/S Connected',
    tone: 'default',
    terminal: false,
    Icon: HandshakeIcon,
  },
  {
    id: 'DueDiligence',
    label: 'Due Diligence',
    tone: 'default',
    terminal: false,
    Icon: ScaleIcon,
  },
  { id: 'KycKyb', label: 'KYC/KYB', tone: 'default', terminal: false, Icon: UserCheckIcon },
  {
    id: 'ContractSigned',
    label: 'Contract Signed',
    tone: 'default',
    terminal: false,
    Icon: SignatureIcon,
  },
  {
    id: 'MoneySent',
    label: 'Money Sent',
    tone: 'default',
    terminal: false,
    Icon: CircleDollarSignIcon,
  },
  {
    id: 'InvoiceSent',
    label: 'Invoice Sent',
    tone: 'default',
    terminal: false,
    Icon: ReceiptTextIcon,
  },
  {
    id: 'FeesCollected',
    label: 'Fees Collected',
    tone: 'success',
    terminal: true,
    Icon: CircleCheckBigIcon,
  },
  {
    id: 'DealCanceled',
    label: 'Deal Canceled',
    tone: 'error',
    terminal: true,
    Icon: CircleXIcon,
  },
  {
    id: 'OfferDeleted',
    label: 'Offer deleted',
    tone: 'error',
    terminal: true,
    systemOnly: true,
    Icon: CircleXIcon,
  },
];

/**
 * Index map rather than a scan per call: both lookups run many times per render
 * — the drawer's timeline walks every stage and asks for the order of each,
 * and every gated field asks twice more.
 */
const STAGE_INDEX_BY_ID = new Map(DEAL_KANBAN_STAGES.map((stage, index) => [stage.id, index]));

export const getStageConfig = (stage: DealStage): DealKanbanStageConfig =>
  DEAL_KANBAN_STAGES[STAGE_INDEX_BY_ID.get(stage) ?? 0];

/** -1 for an unknown stage, matching `findIndex` */
export const getStageOrder = (stage: DealStage): number => STAGE_INDEX_BY_ID.get(stage) ?? -1;

/** Stages a card can be created at by hand: every stage except the cancellation and system ones. */
export const canCreateCardAt = (stage: DealStage): boolean =>
  stage !== 'DealCanceled' && !getStageConfig(stage).systemOnly;

export const MANUAL_CARD_STAGES = DEAL_KANBAN_STAGES.filter((stage) => canCreateCardAt(stage.id));

export const SOURCE_CONFIG: Record<DealKanbanSource, { badge: string; label: string }> = {
  Inquiry: { badge: 'IN', label: 'Inquiry' },
  DirectLink: { badge: 'DL', label: 'Direct Link' },
  ManualMatch: { badge: 'MM', label: 'Manual Match' },
};

export const STRUCTURE_CONFIG: Record<DealKanbanStructure, { badge: string; label: string }> = {
  Direct: { badge: 'DIRECT', label: 'Direct' },
  Layer1: { badge: 'LAYER 1', label: 'Layer 1' },
  Layer2: { badge: 'LAYER 2', label: 'Layer 2' },
};

/** Time-in-stage thresholds (days): green < 7, amber 7–30, red > 30 */
export const TIME_IN_STAGE_AMBER_FROM_DAYS = 7;
export const TIME_IN_STAGE_RED_FROM_DAYS = 30;

export const DEALS_KANBAN_TITLE = 'Deals Kanban';

/** "Expand all" persists across sessions (per design annotation) */
export const DEALS_KANBAN_EXPAND_ALL_STORAGE_KEY = 'deals-kanban.expand-all';

export const INTRODUCER_NONE_OPTION = 'None';

export const INVOICE_MAX_FILE_SIZE_MB = 25;
